import * as React from 'react';
import Helmet from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { Redirect, useParams } from 'react-router-dom';

import { ISetPassword } from '@models';
import config from '@configs/config.json';
import {
  getProjectDepDefaultAuthRoute,
  IProjectPicker,
  RequestStatus,
} from '@store/types';

import './SetPassword.scss';
import { useSelector } from 'react-redux';
import { IApplicationState } from '@store/reducer';
import { acceptShowTermsOfService, showAlmostThere } from '../routes/Routes';

export interface ISetPasswordStateProps {
  error?: string;
  status: RequestStatus;
  projectPicker: IProjectPicker;
}

export interface ISetPasswordDispatchProps {
  onSetPassword: (setPasswordParams: ISetPassword) => void;
}

const SetPassword: React.FC<
  ISetPasswordDispatchProps & ISetPasswordStateProps
> = ({ onSetPassword, error, status, projectPicker }) => {
  const { token } = useParams<{ token: string }>();
  const { t } = useTranslation();
  const [password, setPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [errors, setErrors] = React.useState<string[]>([]);
  const auth = useSelector((state: IApplicationState) => state.authentication);
  const appErrors = useSelector(
    (state: IApplicationState) => state.error.errors
  );

  let skipProjectPicker = React.useMemo(() => {
    return (
      showAlmostThere(auth.status, appErrors, auth.user) ||
      acceptShowTermsOfService(auth.status, appErrors)
    );
  }, [auth, appErrors]);
  const submitPassword = (e: React.MouseEvent) => {
    e.preventDefault();

    const validationErrors: string[] = [];
    if (password !== confirmPassword) {
      validationErrors.push(t('setPassword_error_passwordMismatch'));
    } else if (password.length < config.password.minCharacters) {
      validationErrors.push(
        t('setPassword_error_passwordTooShort', {
          length: config.password.minCharacters,
        })
      );
    } else if (password.length > config.password.maxCharacters) {
      validationErrors.push(
        t('setPassword_error_passwordTooLong', {
          length: config.password.maxCharacters,
        })
      );
    } else {
      var met = 0;
      if (password.match(/[A-Z]/g)) met++;
      if (password.match(/[a-z]/g)) met++;
      if (password.match(/[0-9]/g)) met++;
      if (password.match(/[^A-Za-z0-9]/g)) met++;
      if (met < 2) {
        validationErrors.push(
          t('setPassword_error_passwordRequirementsNotMet')
        );
      }
    }

    setErrors(validationErrors);

    if (!validationErrors.length) {
      onSetPassword({ token: token as string, password });
    }
  };

  const allErrors = errors.slice();
  if (error) {
    allErrors.push(t(error));
  }

  return (
    <div className='login set-password'>
      <Helmet>
        <title>{t('setPassword_title')}</title>
      </Helmet>
      {status === RequestStatus.completed &&
        (skipProjectPicker ||
          projectPicker.getProjectsStatus === RequestStatus.completed) && (
          <Redirect
            to={{ pathname: getProjectDepDefaultAuthRoute(projectPicker) }}
          />
        )}
      <div>
        <h1>{t('setPassword_heading')}</h1>
        <div className='content mx-auto'>
          <p className='mt-4'>
            {t('setPassword_minimumCharacters', {
              length: config.password.minCharacters,
            })}
          </p>
          <p className='mt-4'>{t('setPassword_requirements')}</p>
          <p className='mt-4'>{t('setPassword_caseSensitive')}</p>
          {allErrors.map((error) => (
            <div className='error mb-2' key={error}>
              {error}
            </div>
          ))}
          <form>
            <div className='form-group'>
              <label htmlFor='passwordInput'>{t('login_password')}</label>
              <input
                type='password'
                value={password}
                autoComplete='new-password'
                onChange={(e) => setPassword(e.target.value)}
                className='form-control'
                id='passwordInput'
              />
            </div>
            <div className='form-group'>
              <label htmlFor='confirmPasswordInput'>
                {t('setPassword_reEnterPassword')}
              </label>
              <input
                type='password'
                value={confirmPassword}
                autoComplete='new-password'
                onChange={(e) => setConfirmPassword(e.target.value)}
                className='form-control'
                id='confirmPasswordInput'
              />
            </div>
            <div className='text-right'>
              {status === RequestStatus.inProgress ? (
                <button className='btn btn-dark btn-sm' disabled>
                  <span
                    className='spinner-border spinner-border-sm mr-2'
                    role='status'
                    aria-hidden='true'
                  ></span>
                  {t('setPassword_button')}
                </button>
              ) : (
                <button
                  className='btn btn-primary btn-sm'
                  onClick={(e) => submitPassword(e)}
                >
                  {t('setPassword_button')}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SetPassword;
